import { useApolloClient } from '@apollo/client';
import { OpenInSearchButton } from '@modules/common/components/OpenInSearchButton';
import { deleteAllCachedQueries } from '@modules/common/utils';
import { isCachedData } from '@modules/common/utils/isCachedData';
import { FeedbackList } from '@modules/feedback/components/FeedbackList';
import { FeedbackListHeader } from '@modules/feedback/components/FeedbackListDrawer/FeedbackListHeader';
import {
  EQL_QUERIES,
  SELECT_RECORDS,
  SHOULD_SHOW_FEEDBACK_LIST_SUMMARY_VIEW,
} from '@modules/feedback/constants';
import { useGetFeedbackList } from '@modules/feedback/hooks/useGetFeedbackList';
import { useFeedbackOnFeedbackStore } from '@modules/feedback/store';
import { FeedbackOnFeedbackActionType } from '@modules/feedback/store/feedbackOnFeedbackActionType';
import { FeedbackListStoreProvider } from '@modules/feedback/store/useFeedbackListStore';
import { FeedbackListSummaryContainer } from '@modules/oracle/components/FeedbackListSummaryContainer';
import { Box, Drawer, Stack } from '@mui/material';
import Locker from 'lockr';
import { ReactNode, useState } from 'react';
import { IsLoading } from 'v2/core/repository/edge-server/types';
import { QueryContainer } from 'v2/core/services/notebook/types';
import { FeedbackMetaData } from 'v2/store/useViewFeedbackStore';
import { FeedbackOptions } from 'v2/views/components/feedbackListViewer/types';
import { FeedbackListDrawerHeader } from './FeedbackListDrawerHeader';

export interface FeedbackListDrawerProps {
  open: boolean;
  header?: {
    renderTitle: () => ReactNode;
    renderDescription: () => ReactNode;
    secondaryActionNode?: ReactNode;
    shouldEnableSort: boolean;
  };
  queryContainer: QueryContainer;
  onClose: () => void;
  options?: FeedbackOptions;
  selectedFeedbackMetadata?: FeedbackMetaData[];
  shouldHideOpenInSearch?: boolean;
}

export function FeedbackListDrawer({
  open,
  onClose,
  header,
  queryContainer,
  options,
  selectedFeedbackMetadata,
  shouldHideOpenInSearch = false,
}: FeedbackListDrawerProps) {
  const dispatch = useFeedbackOnFeedbackStore(({ dispatch }) => dispatch);
  const shouldRefetch = useFeedbackOnFeedbackStore(
    ({ shouldRefetch }) => shouldRefetch,
  );

  const [isSummaryView, setIsSummaryView] = useState<boolean>(
    Locker.get(SHOULD_SHOW_FEEDBACK_LIST_SUMMARY_VIEW) ?? false,
  );

  const getFeedbackListResp = useGetFeedbackList({ queryContainer });

  const handleSummaryViewChange = (isSummaryView: boolean) => {
    setIsSummaryView(isSummaryView);
  };

  const client = useApolloClient();

  const onDrawerClose = () => {
    if (isCachedData(client.cache) && shouldRefetch) {
      deleteAllCachedQueries(client.cache, EQL_QUERIES, SELECT_RECORDS);
      dispatch({
        type: FeedbackOnFeedbackActionType.SET_SHOULD_REFETCH,
        payload: false,
      });
    }
    onClose();
  };

  const renderSecondaryActionNode = (): ReactNode => {
    if (shouldHideOpenInSearch) {
      return header?.secondaryActionNode;
    }

    return (
      <Stack direction="row" alignItems="center" columnGap={1}>
        <Box>{header?.secondaryActionNode}</Box>
        <OpenInSearchButton queryContainer={queryContainer} />
      </Stack>
    );
  };

  return (
    <Drawer open={open} anchor="right" onClose={onDrawerClose}>
      <Stack
        sx={{
          p: 2,
        }}
        width="50vw"
        minWidth="700px"
        spacing={2}
        overflow="hidden"
      >
        {header && (
          <FeedbackListDrawerHeader
            title={header.renderTitle()}
            description={header.renderDescription()}
            secondaryActionNode={renderSecondaryActionNode()}
            queryContainer={queryContainer}
            feedbackCount={getFeedbackListResp.hits}
          />
        )}
        <FeedbackListSummaryContainer queryContainer={queryContainer} />
        <FeedbackListHeader
          isSummaryView={isSummaryView}
          onSummaryViewChange={handleSummaryViewChange}
          feedbackCount={getFeedbackListResp.hits ?? 0}
          isFetchingFeedbackCount={
            !getFeedbackListResp.hits
              ? IsLoading(getFeedbackListResp.status)
              : false
          }
        />
        <Box
          sx={{ maxHeight: '90vh', overflow: 'auto' }}
          id="js-feedback-list-scroll"
        >
          <Box id="js-feedback-list">
            <FeedbackListStoreProvider>
              <FeedbackList
                queryContainer={queryContainer}
                queryString=""
                {...getFeedbackListResp}
                options={options}
                variant="compact"
                selectedFeedbackMetadata={selectedFeedbackMetadata}
                isSummaryView={isSummaryView}
                shouldShowScrollToTopButton={false}
              />
            </FeedbackListStoreProvider>
          </Box>
        </Box>
      </Stack>
    </Drawer>
  );
}
